import React from 'react';
import { Search } from 'lucide-react';
import { DateRangeField } from '../common/DatePickerFields';

export type DateFilter = { mode: 'all' | 'today' | '7d' | '30d' | 'custom'; from: string; to: string };
export const defaultDateFilter: DateFilter = { mode: 'all', from: '', to: '' };

function jkt(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' });
  } catch {
    return '';
  }
}

export function filterByDate<T extends { created_at?: string }>(items: T[], f: DateFilter): T[] {
  if (f.mode === 'all') return items;
  if (f.mode === 'today') {
    const today = jkt(new Date().toISOString());
    return items.filter((i) => jkt(i.created_at) === today);
  }
  if (f.mode === 'custom') {
    return items.filter((i) => {
      const d = jkt(i.created_at);
      if (!d) return false;
      if (f.from && d < f.from) return false;
      if (f.to && d > f.to) return false;
      return true;
    });
  }
  const days = f.mode === '7d' ? 7 : 30;
  const cutoff = Date.now() - days * 86400000;
  return items.filter((i) => (i.created_at ? new Date(i.created_at).getTime() : 0) >= cutoff);
}

const chip = (active: boolean) =>
  `text-xs px-3 py-1.5 rounded-lg border transition-colors ${active ? 'bg-cyan-400 text-[#050914] border-cyan-400 font-medium' : 'border-white/15 text-white/70 hover:bg-white/5'}`;

export function SignalFilterBar({ value, onChange, search, onSearch }: { value: DateFilter; onChange: (v: DateFilter) => void; search?: string; onSearch?: (v: string) => void }) {
  const set = (mode: DateFilter['mode']) => onChange({ ...value, mode });
  return (
    <div className="flex flex-col gap-3 mb-5" data-testid="signal-filter-bar">
      {onSearch && (
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <input
            data-testid="signal-search"
            value={search || ''}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Cari instrument (XAUUSD, EURUSD...)"
            className="w-full bg-[#050914] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition-colors"
          />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="signal-filter-all" onClick={() => set('all')} className={chip(value.mode === 'all')}>Semua</button>
        <button data-testid="signal-filter-today" onClick={() => set('today')} className={chip(value.mode === 'today')}>Hari Ini</button>
        <button data-testid="signal-filter-7d" onClick={() => set('7d')} className={chip(value.mode === '7d')}>7 Hari</button>
        <button data-testid="signal-filter-30d" onClick={() => set('30d')} className={chip(value.mode === '30d')}>30 Hari</button>
        <button data-testid="signal-filter-custom" onClick={() => set('custom')} className={chip(value.mode === 'custom')}>Custom</button>
      </div>
      {value.mode === 'custom' && (
        <div className="w-full sm:w-80" data-testid="signal-custom-range">
          <DateRangeField
            testId="signal-date-range"
            from={value.from}
            to={value.to}
            onChange={({ from, to }) => onChange({ ...value, from, to })}
            placeholder="Pilih rentang tanggal"
          />
        </div>
      )}
    </div>
  );
}
